import * as React from 'react';
import StandLayer from './StandLayer';
import RBush from 'rbush';
import * as turf from '@turf/turf';
import LayerParams from './LayerParams';
const useOptimizedStandLayers = (map, stands, baseLayerChangedNum, options) => {
    const [standLayers, setStandLayers] = React.useState([]);
    // when stands change, create new stand layers
    React.useEffect(() => {
        if (!map || !stands)
            return;
        const tree = new RBush();
        tree.load(stands.map(stand => {
            const bb = turf.bbox(stand.geoJSON.geometry);
            return bb && { minX: bb[0], minY: bb[1], maxX: bb[2], maxY: bb[3], stand };
        }));
        const filterStand = () => {
            var _a, _b;
            if (map.getZoom() < ((_a = options === null || options === void 0 ? void 0 : options.zoomFilter) !== null && _a !== void 0 ? _a : 16)) {
                setStandLayers([]);
                return;
            }
            const mapBB = map.getBounds().toArray();
            if (!mapBB)
                return;
            const abroadDraw = ((_b = options === null || options === void 0 ? void 0 : options.abroadDraw) !== null && _b !== void 0 ? _b : 0) / 100;
            const standsInMapScreen = tree.search({
                minX: mapBB[0][0] - ((mapBB[1][0] - mapBB[0][0]) * abroadDraw),
                minY: mapBB[0][1] - ((mapBB[1][1] - mapBB[0][1]) * abroadDraw),
                maxX: mapBB[1][0] + ((mapBB[1][0] - mapBB[0][0]) * abroadDraw),
                maxY: mapBB[1][1] + ((mapBB[1][1] - mapBB[0][1]) * abroadDraw),
            }).map(ti => ti.stand);
            setStandLayers(makeUniq(standsInMapScreen).map(stand => new StandLayer({ stand })));
        };
        const listener = (eventData) => {
            var _a, _b;
            if (eventData.type === 'moveend' ||
                (((_a = eventData.originalEvent) === null || _a === void 0 ? void 0 : _a.type) !== undefined && ((_b = eventData.originalEvent) === null || _b === void 0 ? void 0 : _b.type) !== 'click')) {
                filterStand();
            }
        };
        map.on('moveend', listener);
        map.on('load', filterStand);
        filterStand();
        return () => {
            map.off('moveend', listener);
            map.off('load', filterStand);
        };
    }, [map, stands]);
    React.useEffect(() => {
        if (!map || baseLayerChangedNum === 0)
            return;
        const sourceId = LayerParams.sourceId;
        const standFillsLayerId = LayerParams.standFill;
        const standBordersLayerId = LayerParams.standBorder;
        map.addSource(sourceId, {
            type: 'geojson',
            promoteId: 'id',
            data: {
                type: 'FeatureCollection',
                features: stands.map(s => {
                    let newGeoJSON = Object.assign({}, s.geoJSON);
                    newGeoJSON.properties = Object.assign(Object.assign({}, s), { fillColor: s.disabled ? '#404040' : '#9CDB8A', lineColor: s.disabled ? '#404040' : 'green', lineWidth: s.disabled ? 1 : 3 });
                    return newGeoJSON;
                }),
            },
        });
        map.addLayer({
            id: standFillsLayerId,
            type: 'fill',
            source: sourceId,
            layout: {},
            paint: {
                'fill-color': ['get', 'fillColor'],
                'fill-opacity': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    0.2,
                    0.1
                ]
            }
        });
        map.addLayer({
            id: standBordersLayerId,
            type: 'line',
            source: sourceId,
            layout: {
                'line-cap': 'butt',
                'line-join': 'round',
            },
            paint: {
                'line-width': ['get', 'lineWidth'],
                'line-offset': [
                    'case',
                    ['boolean', ['feature-state', 'selected'], false],
                    1.4,
                    1.4
                ],
                'line-color': [
                    'case',
                    ['boolean', ['feature-state', 'selected'], false],
                    '#FF7417',
                    ['get', 'lineColor']
                ],
                'line-opacity': 1,
            }
        });
        return () => {
            if (map.getLayer(standBordersLayerId)) {
                map.removeLayer(standBordersLayerId);
            }
            if (map.getSource(standBordersLayerId)) {
                map.removeSource(standBordersLayerId);
            }
            if (map.getLayer(standFillsLayerId)) {
                map.removeLayer(standFillsLayerId);
            }
            if (map.getSource(standFillsLayerId)) {
                map.removeSource(standFillsLayerId);
            }
            if (map.getSource(sourceId)) {
                map.removeSource(sourceId);
            }
        };
    }, [map, stands, baseLayerChangedNum]);
    // when base layer is changed at least once,
    // update stand layers — remove from map / add to map
    React.useEffect(() => {
        if (!map)
            return;
        if (baseLayerChangedNum > 0) {
            standLayers.forEach(standLayer => standLayer.addToMap(map));
        }
        return () => {
            standLayers.forEach(standLayer => standLayer.removeFromMap());
        };
    }, [map, standLayers, baseLayerChangedNum]);
    return standLayers;
};
export default useOptimizedStandLayers;
const makeUniq = (stands) => {
    // TODO: Delete me when fix import without duplicate
    const filteredStands = [];
    stands.forEach(stand => {
        if (!filteredStands.find(fs => JSON.stringify(fs.geoJSON) === JSON.stringify(stand.geoJSON)))
            filteredStands.push(stand);
    });
    /** END TODO **/
    return filteredStands;
};
